import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, DollarSign } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/currency';

interface CampaignExpensesTabProps {
  newExpense: any;
  setNewExpense: (expense: any) => void;
  handleAddExpense: () => void;
  addExpenseMutation: any;
  loadingExpenses: boolean;
  expensesData: any[];
  handleDeleteExpenseClick: (id: string) => void;
  deleteExpenseMutation: any;
}

export const CampaignExpensesTab: React.FC<CampaignExpensesTabProps> = ({
  newExpense,
  setNewExpense,
  handleAddExpense,
  addExpenseMutation,
  loadingExpenses,
  expensesData,
  handleDeleteExpenseClick,
  deleteExpenseMutation
}) => {
  const { userCountry } = useAuth();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Column - Entry Form */}
      <div>
        <h3 className="text-lg font-medium mb-4">Add New Expense</h3>
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <Input
              type="date"
              value={newExpense.date}
              onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
            <Input
              type="number"
              placeholder="0"
              value={newExpense.amount}
              onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md"
              value={newExpense.category}
              onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
            >
              <option value="">Select category</option>
              <option value="Logistics">Logistics</option>
              <option value="Gifting">Gifting</option>
              <option value="Courier">Courier</option>
              <option value="Props">Props</option>
              <option value="Travel">Travel</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <Input
              type="text"
              placeholder="Brief description"
              value={newExpense.description}
              onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor (Optional)</label>
            <Input
              type="text"
              placeholder="Vendor name"
              value={newExpense.vendor}
              onChange={(e) => setNewExpense({ ...newExpense, vendor: e.target.value })}
            />
          </div>

          <Button
            onClick={handleAddExpense}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            disabled={addExpenseMutation.isPending || !newExpense.date || !newExpense.amount || !newExpense.category}
          >
            <Plus className="w-4 h-4 mr-2" />
            {addExpenseMutation.isPending ? 'Adding...' : 'Add Expense'}
          </Button>
        </div>
      </div>

      {/* Right Column - Expense History */}
      <div>
        <h3 className="text-lg font-medium mb-4">Expense History</h3>
        {loadingExpenses ? (
          <div className="text-center py-8">Loading expenses...</div>
        ) : (
          <div className="space-y-3">
            {expensesData.map((expense) => (
              <div key={expense.id} className="border rounded-lg p-4 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{expense.category}</span>
                      <Badge variant="outline" className="text-xs">{formatCurrency(expense.amount, userCountry)}</Badge>
                    </div>
                    <p className="text-sm text-gray-600">{expense.description || 'No description'}</p>
                    {expense.vendor && (
                      <p className="text-xs text-gray-500">Vendor: {expense.vendor}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{new Date(expense.date).toLocaleDateString()}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteExpenseClick(expense.id)}
                      disabled={deleteExpenseMutation.isPending}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {expensesData.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No expenses recorded yet</p>
                <p className="text-sm">Add expenses to track campaign costs</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
